import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { styled } from "styled-components";

import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Link, Panel } from "capitalrx-components-2.0/lib/general";
import { Body, Italic } from "capitalrx-components-2.0/lib/typography";
import { formatZip } from "capitalrx-utilities";

import { DrugDetailsResponse, DrugPrice, PharmacyInfo } from "@/api/types";
import { StackOrShelf } from "@/common/components/StackOrShelf";
import { getAppleMapsURL } from "@/basic/components/AppleMap";
import { VerticalDivider } from "@/basic/components/VerticalDivider";
import { ThemedButton } from "@/premium/themedComponents";
import { useNavigateSearch } from "@/premium/hooks";

const ShadedCard = styled(Panel)`
  background-color: #F6F8F9;
  border: none;
  padding: 1rem 1rem;
`;

const StyledLink = styled(Link)`
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
`;

const PreferredStyledTag = styled.div`
  border-radius: 4px;
  background: #313e48;
  color: white;
  padding: 4px;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  white-space: nowrap;
`;

const formatTextPascal = (words: string = "") =>
  words
    .split(" ")
    .filter((word) => word) // Filter out any empty strings.
    .map((word) => word[0].toUpperCase() + word.substring(1).toLowerCase())
    .join(" ");

export interface CombinedPharmacyAndDrugInfo {
  readonly pharmacy: PharmacyInfo,
  readonly drug: DrugPrice & DrugDetailsResponse
};
export interface PharmacyAndDrugProps {
  readonly pharmacyData: CombinedPharmacyAndDrugInfo
  readonly isNoDeductible?: boolean;
}

export const PharmacyCard = ({ pharmacyData, isNoDeductible }: PharmacyAndDrugProps) => {
  const { t: translate } = useTranslation();
  const navigate = useNavigateSearch();
  const [searchParams] = useSearchParams();
  const enrollmentPlanId = searchParams.get("enrollmentPlan");
  const clientId = searchParams.get("client");

  const pharmacy = pharmacyData?.pharmacy;
  const drug = pharmacyData?.drug;

  const { isMobile } = useResponsive(
    useMemo(() => {
      return {
        isMobile: { desktop: false, tablet: false, mobile: true },
      };
    }, []),
  );

  const priceDisplay = useMemo(
    () => {
      if(!drug) {
        return;
      }
      let patientPayAmount = drug?.patient_pay_amount;
      if (isNoDeductible) {
        patientPayAmount = drug?.patient_pay_amount_no_deductible;
        if (patientPayAmount === undefined || patientPayAmount === null) {
          patientPayAmount = drug?.patient_pay_amount;
        }
      }
      if (patientPayAmount === undefined || patientPayAmount === null) {
        return null;
      } else {
        return "$" + patientPayAmount.toFixed(2).toString();
      }
    },
    [isNoDeductible],
  );

  const address = `${formatTextPascal(pharmacy?.address_line_1)}, ${
    pharmacy?.address_line_2 && `${formatTextPascal(pharmacy?.address_line_2)} , `
  }${formatTextPascal(pharmacy?.city)}, ${pharmacy?.state}, ${formatZip(pharmacy?.zip)}`;
  const url = getAppleMapsURL({
    latitude: parseFloat(pharmacy?.latitude),
    longitude: parseFloat(pharmacy?.longitude),
    title: pharmacy?.name?.split("#")?.[0],
    data: {
      address,
    },
  });

  const handleDrugPriceButtonClick = () => {
    navigate("/plan/find-pharmacies/pharmacies/pharmacy-details",
      { enrollmentPlan: enrollmentPlanId,
        client: clientId,
        providerId: pharmacy?.provider_id,
        dosage: drug?.dosage,
        dosageForm: drug?.dosage_form,
        daysSupply: drug?.days_supply,
        quantity: drug?.quantity,
        drugName: drug?.drug_name,
        drugType: drug?.drug_type,
        patientPay: encodeURIComponent(drug?.patient_pay_amount || "0"),
        planPay: encodeURIComponent(drug?.plan_pay_amount || "0"),
      });
  };

  return (
    <ShadedCard>
      <StackOrShelf>
        <Stack spacing="small">
          <Shelf>
            {pharmacy?.pharmacy_display_rules?.display_tag && <PreferredStyledTag>{pharmacy.pharmacy_display_rules.display_tag}</PreferredStyledTag>}
            <StyledLink to={url}>{pharmacy?.name}</StyledLink>
            <Shelf.Spacer />
          </Shelf>
          {pharmacy?.pharmacy_display_rules?.description && (
            <Body size="small"><Italic>{pharmacy?.pharmacy_display_rules?.description}</Italic></Body>
          )}
          <Shelf spacing="small">
            <Body size="small">{(Math.round(Number(pharmacy?.distance) * 100) / 100).toFixed(2)} {translate("PHARMACIES.pharmacyCard.miles")}</Body>
            <VerticalDivider />
            <Body size="small">{formatTextPascal(pharmacy?.address_line_1)}</Body>
            <Shelf.Spacer />
          </Shelf>
        </Stack>
        <Shelf.Spacer />
        {isMobile && drug && <Body size="small">{translate("FIND_PHARMACIES.findPharmacies.estimatedAmountYouWillPay")}:</Body>}
        {
          drug && priceDisplay && (
            <ThemedButton icon="chevron-right" iconPosition="right" onClick={handleDrugPriceButtonClick}>
              {priceDisplay}
            </ThemedButton>
          )
        }
        {
          drug && !priceDisplay && (
            <Body>{translate("FIND_PHARMACIES.findPharmacies.pricingUnavailable")}</Body>
          )
        }
      </StackOrShelf>
    </ShadedCard>
  );
};
